import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Badge, Spinner, Row, Col, Card, CardBody, CardTitle, CardText,
  Input, Label, Button, Table, Form, FormGroup,
} from 'reactstrap';
import { PageHeader } from '../components/PageHeader';
import { OverviewGrid } from '../components/OverviewGrid';
import { probeFetch, isProbeProxyEnabled } from '../probe/probeClient';

/**
 * Probe API — task-basierte Scraping-Engine (Manager-Control-Plane).
 * v0: Manager ist live (Ingest + Queue + State + Callback + Watchdog), der
 * Playwright-Worker folgt in v1 → eingereichte Task-Groups laufen an, bleiben
 * aber `running`/`pending` bis die Worker existieren. Diese Views sind gegen
 * die echten `/v1/admin/*`-Endpoints verdrahtet.
 */

function ProxyGuard({ children }) {
  if (!isProbeProxyEnabled()) {
    return (
      <Alert color="secondary" className="small">
        <strong>Probe-Proxy nicht konfiguriert.</strong> Setze <code>PROBE_API_URL</code> und
        <code> PROBE_API_TOKEN</code> auf dem Orbit-Frontend, damit der BFF <code>/proxy/probe</code>
        aktiv wird.
      </Alert>
    );
  }
  return children;
}

function StatusBadge({ status }) {
  const color = {
    completed: 'success', partial: 'warning', failed: 'danger', timeout: 'danger',
    running: 'info', pending: 'secondary', cancelled: 'dark',
  }[status] || 'secondary';
  return <Badge color={color} pill>{status}</Badge>;
}

// ── Übersicht ──────────────────────────────────────────────────────────
const CARDS = [
  { to: '/admin/settings/probe/scrape', icon: '🎯', title: 'Scraping', status: 'live',
    description: 'Task-Groups überwachen + Scrape-Defaults (Routing/Regionen/Retries) pro Projekt.' },
  { to: '/admin/settings/probe/wireguard', icon: '🔒', title: 'WireGuard', status: 'live',
    description: 'Residential/Mobile-Proxy-Peers der Worker — Registry, Regionen, Handshake.' },
  { to: '/admin/settings/probe/keda', icon: '📐', title: 'KEDA', status: 'live',
    description: 'Worker-Autoscaling: ScaledObject-Trigger auf der BullMQ-Wait-Liste.' },
];

function useOverview() {
  return useQuery({
    queryKey: ['probe-overview'],
    queryFn: () => probeFetch('/v1/admin/overview'),
    refetchInterval: 20_000,
    retry: false,
    enabled: isProbeProxyEnabled(),
  });
}

export function ProbeOverviewPage() {
  const q = useOverview();
  const g = q.data?.groups;
  const queue = q.data?.queue;
  return (
    <div>
      <PageHeader title="Probe API · Übersicht"
        subtitle="Task-basierte Scraping-Engine. Manager live (v0); Playwright-Worker folgt (v1)." />
      <ProxyGuard>
        <Alert color={q.isError ? 'danger' : 'light'} className="d-flex align-items-center gap-2 small">
          <strong>API-Status:</strong>
          {q.isLoading ? <><Spinner size="sm" /> prüfe …</>
            : q.isError ? <Badge color="danger" pill>nicht erreichbar</Badge>
            : <Badge color="success" pill>erreichbar</Badge>}
        </Alert>
        {q.data && (
          <Row className="g-3 mb-3">
            <Col md={3}><Card><CardBody><CardTitle tag="h6">Task-Groups</CardTitle>
              <CardText className="fs-4">{g?.total ?? 0}</CardText>
              <div className="small text-muted">{g?.running ?? 0} laufend · {g?.completed ?? 0} fertig · {g?.failed ?? 0} fehlgeschlagen</div>
            </CardBody></Card></Col>
            <Col md={3}><Card><CardBody><CardTitle tag="h6">Queue-Tiefe</CardTitle>
              <CardText className="fs-4">{(queue?.wait ?? 0) + (queue?.active ?? 0) + (queue?.delayed ?? 0)}</CardText>
              <div className="small text-muted">{queue?.wait ?? 0} wartend · {queue?.active ?? 0} aktiv</div>
            </CardBody></Card></Col>
            <Col md={3}><Card><CardBody><CardTitle tag="h6">Worker</CardTitle>
              <CardText className="fs-4">{q.data.workers ?? 0}</CardText>
              <div className="small text-muted">KEDA scale-to-zero (v1)</div>
            </CardBody></Card></Col>
            <Col md={3}><Card><CardBody><CardTitle tag="h6">WireGuard-Peers</CardTitle>
              <CardText className="fs-4">{q.data.wireguardPeers?.active ?? 0}<span className="fs-6 text-muted">/{q.data.wireguardPeers?.total ?? 0}</span></CardText>
              <div className="small text-muted">aktiv / gesamt</div>
            </CardBody></Card></Col>
          </Row>
        )}
        <OverviewGrid cards={CARDS} />
      </ProxyGuard>
    </div>
  );
}

// ── Scraping (Task-Groups + Defaults) ───────────────────────────────────
function useTasks() {
  return useQuery({
    queryKey: ['probe-tasks'],
    queryFn: () => probeFetch('/v1/admin/tasks?limit=50'),
    refetchInterval: 15_000, retry: false, enabled: isProbeProxyEnabled(),
  });
}
function useDefaults() {
  return useQuery({
    queryKey: ['probe-defaults'],
    queryFn: () => probeFetch('/v1/admin/scrape-defaults'),
    retry: false, enabled: isProbeProxyEnabled(),
  });
}

export function ProbeScrapePage() {
  const tasks = useTasks();
  const defaults = useDefaults();
  const qc = useQueryClient();
  const [form, setForm] = useState({ project: '', routing_type: 'datacenter', allowed_regions: 'DE', max_retries: 2 });

  const save = useMutation({
    mutationFn: (body) => probeFetch('/v1/admin/scrape-defaults', { method: 'PUT', body }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['probe-defaults'] }); },
  });

  return (
    <div>
      <PageHeader title="Probe · Scraping" subtitle="Task-Groups + projektweite Scrape-Defaults." />
      <ProxyGuard>
        <Card className="mb-3"><CardBody>
          <CardTitle tag="h6">Task-Groups (aktuellste 50)</CardTitle>
          {tasks.isLoading ? <Spinner size="sm" />
            : tasks.isError ? <Alert color="danger" className="small">Laden fehlgeschlagen.</Alert>
            : !tasks.data?.items?.length ? <CardText className="small text-muted">Noch keine Task-Groups eingereicht.</CardText>
            : (
              <Table size="sm" responsive hover>
                <thead><tr><th>group_id</th><th>Projekt</th><th>Status</th><th>Tasks</th><th>Erstellt</th></tr></thead>
                <tbody>
                  {tasks.data.items.map((t) => (
                    <tr key={t.group_id}>
                      <td><code>{t.group_id}</code></td>
                      <td>{t.project}</td>
                      <td><StatusBadge status={t.status} /></td>
                      <td className="small">{t.completed_tasks}/{t.total_tasks}{t.failed_tasks ? ` (${t.failed_tasks} ✗)` : ''}</td>
                      <td className="small text-muted">{new Date(t.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
        </CardBody></Card>

        <Card><CardBody>
          <CardTitle tag="h6">Scrape-Defaults pro Projekt</CardTitle>
          {defaults.data?.items?.length ? (
            <Table size="sm" responsive className="mb-3">
              <thead><tr><th>Projekt</th><th>Routing</th><th>Regionen</th><th>Retries</th><th>TTL (s)</th></tr></thead>
              <tbody>
                {defaults.data.items.map((d) => (
                  <tr key={d.project}>
                    <td>{d.project}</td><td>{d.routing_type}</td>
                    <td className="small">{Array.isArray(d.allowed_regions) ? d.allowed_regions.join(', ') : d.allowed_regions}</td>
                    <td>{d.max_retries}</td><td>{d.max_group_ttl_sec}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : <CardText className="small text-muted">Keine Defaults gesetzt.</CardText>}

          <Form onSubmit={(e) => {
            e.preventDefault();
            save.mutate({
              project: form.project,
              routing_type: form.routing_type,
              allowed_regions: form.allowed_regions.split(',').map((s) => s.trim()).filter(Boolean),
              max_retries: Number(form.max_retries),
            });
          }}>
            <Row className="g-2 align-items-end">
              <Col md={3}><Label className="small">Projekt</Label>
                <Input bsSize="sm" required value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} /></Col>
              <Col md={2}><Label className="small">Routing</Label>
                <Input type="select" bsSize="sm" value={form.routing_type} onChange={(e) => setForm({ ...form, routing_type: e.target.value })}>
                  <option>datacenter</option><option>residential</option><option>mobile</option><option>tor</option>
                </Input></Col>
              <Col md={3}><Label className="small">Regionen (CSV)</Label>
                <Input bsSize="sm" value={form.allowed_regions} onChange={(e) => setForm({ ...form, allowed_regions: e.target.value })} /></Col>
              <Col md={2}><Label className="small">Max Retries</Label>
                <Input type="number" bsSize="sm" value={form.max_retries} onChange={(e) => setForm({ ...form, max_retries: e.target.value })} /></Col>
              <Col md={2}><Button size="sm" color="primary" type="submit" disabled={save.isPending}>Speichern</Button></Col>
            </Row>
            {save.isError && <Alert color="danger" className="small mt-2">{save.error?.message}</Alert>}
            {save.isSuccess && <Alert color="success" className="small mt-2">Gespeichert.</Alert>}
          </Form>
        </CardBody></Card>
      </ProxyGuard>
    </div>
  );
}

// ── WireGuard ────────────────────────────────────────────────────────────
function usePeers() {
  return useQuery({
    queryKey: ['probe-peers'],
    queryFn: () => probeFetch('/v1/admin/wireguard/peers'),
    retry: false, enabled: isProbeProxyEnabled(),
  });
}

export function ProbeWireguardPage() {
  const peers = usePeers();
  const qc = useQueryClient();
  const [form, setForm] = useState({ label: '', region: 'DE', routing_type: 'residential', public_key: '', endpoint: '' });

  const add = useMutation({
    mutationFn: (body) => probeFetch('/v1/admin/wireguard/peers', { method: 'POST', body }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['probe-peers'] }); setForm({ label: '', region: 'DE', routing_type: 'residential', public_key: '', endpoint: '' }); },
  });
  const del = useMutation({
    mutationFn: (id) => probeFetch(`/v1/admin/wireguard/peers/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['probe-peers'] }),
  });

  return (
    <div>
      <PageHeader title="Probe · WireGuard" subtitle="Peer-Registry für Residential/Mobile-Routing der Worker." />
      <ProxyGuard>
        <Card className="mb-3"><CardBody>
          <CardTitle tag="h6">Peers</CardTitle>
          {peers.isLoading ? <Spinner size="sm" />
            : !peers.data?.items?.length ? <CardText className="small text-muted">Keine Peers registriert.</CardText>
            : (
              <Table size="sm" responsive hover>
                <thead><tr><th>Label</th><th>Routing</th><th>Region</th><th>PublicKey</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {peers.data.items.map((p) => (
                    <tr key={p.id}>
                      <td>{p.label}</td><td>{p.routing_type}</td><td>{p.region}</td>
                      <td><code className="small">{String(p.public_key).slice(0, 12)}…</code></td>
                      <td><Badge color={p.status === 'active' ? 'success' : p.status === 'disabled' ? 'secondary' : 'warning'} pill>{p.status}</Badge></td>
                      <td><Button size="sm" color="link" className="text-danger p-0" onClick={() => del.mutate(p.id)}>löschen</Button></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
        </CardBody></Card>

        <Card><CardBody>
          <CardTitle tag="h6">Peer hinzufügen</CardTitle>
          <Form onSubmit={(e) => { e.preventDefault(); add.mutate({ ...form, endpoint: form.endpoint || undefined }); }}>
            <Row className="g-2 align-items-end">
              <Col md={2}><Label className="small">Label</Label>
                <Input bsSize="sm" required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} /></Col>
              <Col md={2}><Label className="small">Routing</Label>
                <Input type="select" bsSize="sm" value={form.routing_type} onChange={(e) => setForm({ ...form, routing_type: e.target.value })}>
                  <option>residential</option><option>mobile</option><option>datacenter</option><option>tor</option>
                </Input></Col>
              <Col md={1}><Label className="small">Region</Label>
                <Input bsSize="sm" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} /></Col>
              <Col md={3}><Label className="small">PublicKey</Label>
                <Input bsSize="sm" required value={form.public_key} onChange={(e) => setForm({ ...form, public_key: e.target.value })} /></Col>
              <Col md={2}><Label className="small">Endpoint</Label>
                <Input bsSize="sm" placeholder="host:port" value={form.endpoint} onChange={(e) => setForm({ ...form, endpoint: e.target.value })} /></Col>
              <Col md={2}><Button size="sm" color="primary" type="submit" disabled={add.isPending}>Hinzufügen</Button></Col>
            </Row>
            {add.isError && <Alert color="danger" className="small mt-2">{add.error?.message}</Alert>}
          </Form>
        </CardBody></Card>
      </ProxyGuard>
    </div>
  );
}

// ── KEDA (Live-ScaledObject-Status; Patch folgt in v2) ──────────────────────
function CondBadge({ value }) {
  const v = String(value);
  const color = v === 'True' ? 'success' : v === 'False' ? 'secondary' : 'warning';
  return <Badge color={color} pill>{value ?? '—'}</Badge>;
}

export function ProbeKedaPage() {
  const q = useQuery({
    queryKey: ['probe-keda'],
    queryFn: () => probeFetch('/v1/admin/keda'),
    refetchInterval: 10_000, retry: false, enabled: isProbeProxyEnabled(),
  });
  const d = q.data;
  const live = d?.source === 'live';
  return (
    <div>
      <PageHeader title="Probe · KEDA" subtitle="Worker-Autoscaling anhand der BullMQ-Wait/Active-Listen." />
      <ProxyGuard>
        {q.isLoading ? <Spinner size="sm" />
          : q.isError ? <Alert color="danger" className="small">Laden fehlgeschlagen.</Alert>
          : (
            <>
              <Row className="g-3 mb-3">
                <Col md={3}><Card><CardBody><CardTitle tag="h6">Replicas (aktuell)</CardTitle>
                  <CardText className="fs-4">{d.replicas?.current ?? '–'}<span className="fs-6 text-muted">/{d.spec?.maxReplicaCount ?? '?'}</span></CardText>
                  <div className="small text-muted">{d.replicas?.ready ?? '–'} ready · Ziel {d.replicas?.desired ?? '–'} · min {d.spec?.minReplicaCount ?? '?'}</div>
                </CardBody></Card></Col>
                <Col md={3}><Card><CardBody><CardTitle tag="h6">Queue-Tiefe</CardTitle>
                  <CardText className="fs-4">{d.queueDepth}</CardText>
                  <div className="small text-muted">{d.queue?.wait ?? 0} wartend · {d.queue?.active ?? 0} aktiv</div>
                </CardBody></Card></Col>
                <Col md={3}><Card><CardBody><CardTitle tag="h6">Active</CardTitle>
                  <CardText><CondBadge value={d.status?.active} /></CardText>
                  <div className="small text-muted">Ready <CondBadge value={d.status?.ready} />{d.status?.fallback && <> · Fallback <CondBadge value={d.status.fallback} /></>}</div>
                </CardBody></Card></Col>
                <Col md={3}><Card><CardBody><CardTitle tag="h6">Cooldown / Polling</CardTitle>
                  <CardText className="fs-6">{d.spec?.cooldownPeriod ?? '?'}s / {d.spec?.pollingInterval ?? '?'}s</CardText>
                  <div className="small text-muted">letzte Aktivität: {d.status?.lastActiveTime ? new Date(d.status.lastActiveTime).toLocaleString() : '—'}</div>
                </CardBody></Card></Col>
              </Row>

              <Card><CardBody>
                <CardTitle tag="h6">ScaledObject <code>{d.scaledObject}</code>
                  <Badge color={live ? 'success' : 'secondary'} pill className="ms-2">{live ? 'live (k8s)' : d.source}</Badge>
                  {d.status?.paused && <Badge color="warning" pill className="ms-1">paused</Badge>}
                  {d.status?.hpaName && <span className="text-muted small ms-2">HPA: <code>{d.status.hpaName}</code></span>}
                </CardTitle>

                <Table size="sm" responsive className="mb-2">
                  <thead><tr><th>Trigger</th><th>Liste</th><th>listLength</th>{live && <th>aktueller Wert</th>}</tr></thead>
                  <tbody>
                    {(d.triggers || (d.trigger ? [d.trigger] : [])).map((t, i) => {
                      const m = (d.metrics || []).find((x) => x.name && t.listName && x.name.includes(t.listName.replace(/[^a-z0-9]/gi, '')));
                      return (
                        <tr key={i}>
                          <td className="small">{t.type}</td>
                          <td><code className="small">{t.listName}</code></td>
                          <td>{t.listLength}</td>
                          {live && <td className="small">{m?.value ?? '–'}</td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>

                {live && d.status?.conditions?.length > 0 && (
                  <div className="small text-muted mb-2">
                    {d.status.conditions.map((c) => <span key={c.type} className="me-3">{c.type}: <CondBadge value={c.status} /></span>)}
                  </div>
                )}
                {d.error && <Alert color="warning" className="small mb-2">k8s: {d.error}</Alert>}
                <Alert color="light" className="small mb-0">{d.note}</Alert>
              </CardBody></Card>
            </>
          )}
      </ProxyGuard>
    </div>
  );
}
